import React, { useEffect, useState } from 'react';
import ToggleSwitch from '../ToggleSwitch';
import { ICustomAlertPreference, IDigestAlertPreference, ISmartAlertPreference } from '../../types/api-caller';
import { DeliveryRates } from '../../constants/user-data';

function CheckboxWithLabel({
  checked,
  onChange,
  label,
  disabled = false,
  sectionTitle,
}: {
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
  disabled?: boolean;
  sectionTitle: string;
}) {
  const sanitizedSectionTitle = sectionTitle
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase();
  const sanitizedLabel = label
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase();
  const id = `checkbox-${sanitizedSectionTitle}-${sanitizedLabel}`;
  return (
    <div className="flex flex-row items-center gap-2 min-h-[32px] min-w-0">
      <input
        id={id}
        type="checkbox"
        className="accent-ow-blue h-5 w-5 rounded border-gray-300 cursor-pointer row-span-1"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <label
        htmlFor={id}
        className={`font-normal text-base ${disabled ? 'text-gray-400' : 'text-black'} cursor-pointer self-center break-words`}
      >
        {label}
      </label>
    </div>
  );
}

export interface AlertSectionSettings {
  name?: string;
  enabled: boolean;
  frequency: string | null;
  exclude_low_articles: boolean;
  increase_threshold: number | null;
  decrease_threshold: number | null;
  score_increase_enabled?: boolean;
  score_decrease_enabled?: boolean;
}

interface AlertSettingsSectionProps {
  title: string;
  data: IDigestAlertPreference | ISmartAlertPreference | ICustomAlertPreference | null;
  showFrequency?: boolean;
  showThresholds?: boolean;
  showSmartTypes?: boolean;
  disabled?: boolean;
  onSettingsChanged: ((settings: AlertSectionSettings) => void) | undefined;
  onSaveCustomAlert?: (name: string, settings: AlertSectionSettings) => void;
}

const AlertSettingsSection: React.FC<AlertSettingsSectionProps> = ({
  title,
  data,
  showFrequency,
  showThresholds,
  showSmartTypes,
  disabled = false,
  onSettingsChanged,
  onSaveCustomAlert,
}) => {
  const [alertEnabled, setAlertEnabled] = useState<boolean>(false);
  const [selectedFrequency, setSelectedFrequency] = useState<string>('DAILY');
  const [excludeLowArticles, setExcludeLowArticles] = useState<boolean>(false);
  const [scoreIncreaseAlertEnabled, setScoreIncreaseAlertEnabled] = useState<boolean>(false);
  const [scoreIncreaseValue, setScoreIncreaseValue] = useState<string>('0');
  const [scoreDecreaseAlertEnabled, setScoreDecreaseAlertEnabled] = useState<boolean>(false);
  const [scoreDecreaseValue, setScoreDecreaseValue] = useState<string>('0');
  const [customAlertName, setCustomAlertName] = useState<string>('');
  const isDeferredCustomSection = Boolean(showThresholds && onSaveCustomAlert);

  const buildSettings = ({
    nameValue = customAlertName,
    enabled = alertEnabled,
    frequency = selectedFrequency,
    excludeLowArticlesValue = excludeLowArticles,
    scoreIncreaseEnabled = scoreIncreaseAlertEnabled,
    scoreDecreaseEnabled = scoreDecreaseAlertEnabled,
    scoreIncreaseThresholdValue = scoreIncreaseValue,
    scoreDecreaseThresholdValue = scoreDecreaseValue,
  }: {
    nameValue?: string;
    enabled?: boolean;
    frequency?: string | null;
    excludeLowArticlesValue?: boolean;
    scoreIncreaseEnabled?: boolean;
    scoreDecreaseEnabled?: boolean;
    scoreIncreaseThresholdValue?: string;
    scoreDecreaseThresholdValue?: string;
  } = {}): AlertSectionSettings => {
    const settings: AlertSectionSettings = {
      enabled,
      frequency,
      exclude_low_articles: excludeLowArticlesValue,
      increase_threshold: showThresholds && scoreIncreaseEnabled ? Number(scoreIncreaseThresholdValue) || null : null,
      decrease_threshold: showThresholds && scoreDecreaseEnabled ? Number(scoreDecreaseThresholdValue) || null : null,
    };

    if (showSmartTypes) {
      settings.score_increase_enabled = scoreIncreaseEnabled;
      settings.score_decrease_enabled = scoreDecreaseEnabled;
    }

    if (showThresholds && nameValue.trim()) {
      settings.name = nameValue.trim();
    }

    return settings;
  };

  useEffect(() => {
    if (data) {
      setAlertEnabled(data.enabled);
      setSelectedFrequency('frequency' in data ? data.frequency : 'DAILY');
      setExcludeLowArticles(data.exclude_low_articles || false);

      if (showSmartTypes) {
        setScoreIncreaseAlertEnabled('score_increase_enabled' in data ? data.score_increase_enabled : true);
        setScoreDecreaseAlertEnabled('score_decrease_enabled' in data ? data.score_decrease_enabled : false);
        setScoreIncreaseValue('10');
        setScoreDecreaseValue('10');
      } else {
        setScoreIncreaseAlertEnabled('increase_threshold' in data && data.increase_threshold !== null);
        setScoreIncreaseValue('increase_threshold' in data && data.increase_threshold !== null ? String(data.increase_threshold) : '10');
        setScoreDecreaseAlertEnabled('decrease_threshold' in data && data.decrease_threshold !== null);
        setScoreDecreaseValue('decrease_threshold' in data && data.decrease_threshold !== null ? String(data.decrease_threshold) : '10');
      }
    } else {
      // Default display settings
      setAlertEnabled(false);
      setSelectedFrequency('DAILY');
      setExcludeLowArticles(false);

      // Keep Smart defaults aligned with user email settings.
      setScoreIncreaseAlertEnabled(showSmartTypes ? true : false);
      setScoreIncreaseValue('10');
      setScoreDecreaseAlertEnabled(false);
      setScoreDecreaseValue('10');
    }

    if (isDeferredCustomSection) {
      // Keep name explicit per save action in admin custom replace flow.
      setCustomAlertName('');
    }
  }, [data, showSmartTypes, isDeferredCustomSection]);

  const emitSettingsChanged = (overrides: {
    nameValue?: string;
    enabled?: boolean;
    frequency?: string | null;
    excludeLowArticlesValue?: boolean;
    scoreIncreaseEnabled?: boolean;
    scoreDecreaseEnabled?: boolean;
    scoreIncreaseThresholdValue?: string;
    scoreDecreaseThresholdValue?: string;
  }) => {
    if (isDeferredCustomSection) {
      return;
    }

    onSettingsChanged?.(buildSettings(overrides));
  };

  const handleAlertEnabledChange = (checked: boolean) => {
    setAlertEnabled(checked);

    // If enabling alert and no direction is selected, default to increase enabled.
    const shouldEnableIncreaseDirection =
      checked && (showThresholds || showSmartTypes) && !scoreIncreaseAlertEnabled && !scoreDecreaseAlertEnabled;
    const nextIncreaseEnabled = shouldEnableIncreaseDirection ? true : scoreIncreaseAlertEnabled;

    if (shouldEnableIncreaseDirection) {
      setScoreIncreaseAlertEnabled(true);
    }

    emitSettingsChanged({
      enabled: checked,
      scoreIncreaseEnabled: nextIncreaseEnabled,
    });
  };

  const handleFrequencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFrequency(e.target.value);
    emitSettingsChanged({ frequency: e.target.value });
  };

  const handleExcludeLowArticlesChange = (checked: boolean) => {
    setExcludeLowArticles(checked);
    emitSettingsChanged({ excludeLowArticlesValue: checked });
  };

  const handleScoreIncreaseAlertEnabledChange = (checked: boolean) => {
    // When enabled, at least one of increase or decrease should be checked.
    if (!checked && !scoreDecreaseAlertEnabled && alertEnabled && (showThresholds || showSmartTypes)) {
      return;
    }

    setScoreIncreaseAlertEnabled(checked);
    emitSettingsChanged({ scoreIncreaseEnabled: checked });
  };

  const handleScoreIncreaseValueChange = (value: string) => {
    // Allow empty string for input clearing, or valid numbers 0-100
    if (value === '' || (/^\d{1,3}$/.test(value) && Number(value) >= 0 && Number(value) <= 100)) {
      setScoreIncreaseValue(value);
    }
  };

  const handleScoreIncreaseValueBlur = () => {
    // Update user settings when user clicks away from input
    // Validate input and reset to default if invalid
    if (
      scoreIncreaseValue === '' ||
      isNaN(Number(scoreIncreaseValue)) ||
      Number(scoreIncreaseValue) < 0 ||
      Number(scoreIncreaseValue) > 100
    ) {
      setScoreIncreaseValue('10');
    }

    const finalValue = scoreIncreaseValue === '' || isNaN(Number(scoreIncreaseValue)) ? 10 : Number(scoreIncreaseValue);
    emitSettingsChanged({ scoreIncreaseThresholdValue: String(finalValue) });
  };

  const handleScoreDecreaseAlertEnabledChange = (checked: boolean) => {
    // When enabled, at least one of increase or decrease should be checked.
    if (!checked && !scoreIncreaseAlertEnabled && alertEnabled && (showThresholds || showSmartTypes)) {
      return;
    }

    setScoreDecreaseAlertEnabled(checked);
    emitSettingsChanged({ scoreDecreaseEnabled: checked });
  };

  const handleScoreDecreaseValueChange = (value: string) => {
    // Allow empty string for input clearing, or valid numbers 0-100
    if (value === '' || (/^\d{1,3}$/.test(value) && Number(value) >= 0 && Number(value) <= 100)) {
      setScoreDecreaseValue(value);
    }
  };

  const handleScoreDecreaseValueBlur = () => {
    // Update user settings when user clicks away from input
    // Validate input and reset to default if invalid
    if (
      scoreDecreaseValue === '' ||
      isNaN(Number(scoreDecreaseValue)) ||
      Number(scoreDecreaseValue) < 0 ||
      Number(scoreDecreaseValue) > 100
    ) {
      setScoreDecreaseValue('10');
    }

    const finalValue = scoreDecreaseValue === '' || isNaN(Number(scoreDecreaseValue)) ? 10 : Number(scoreDecreaseValue);
    emitSettingsChanged({ scoreDecreaseThresholdValue: String(finalValue) });
  };

  return (
    <div className={`flex flex-col gap-2 pb-4 transition-opacity ${disabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`}>
      <div className="flex flex-row items-center justify-between">
        <div className={`text-lg font-semibold ${disabled ? 'text-gray-400' : 'text-black'}`}>{title}</div>
        <ToggleSwitch checked={alertEnabled} onChange={handleAlertEnabledChange} disabled={disabled} />
      </div>
      <hr className="mb-0 mt-0" />

      {isDeferredCustomSection ? (
        <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className={`font-normal text-base ${disabled ? 'text-gray-400' : 'text-black'} sm:mr-2`}>Alert Name:</label>
            <input
              type="text"
              value={customAlertName}
              onChange={(e) => setCustomAlertName(e.target.value)}
              placeholder="Enter alert name"
              disabled={disabled}
              className={`w-full sm:flex-1 h-8 border border-gray-300 rounded px-2 py-1 text-base focus:outline-none focus:ring-2 focus:ring-ow-blue ${disabled ? 'text-gray-400' : 'text-black'}`}
            />
          </div>

          <div className={`grid gap-3 ${!alertEnabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`}>
            {showFrequency && (
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <label className={`font-normal text-base ${disabled ? 'text-gray-400' : 'text-black'} cursor-pointer self-center mr-2`}>
                  Frequency:
                </label>
                <div className="flex flex-row flex-wrap gap-4">
                  {Object.keys(DeliveryRates).map((key) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={`frequency-${title}`}
                        value={key}
                        checked={selectedFrequency === key}
                        onChange={handleFrequencyChange}
                        disabled={disabled}
                        className="form-radio h-5 w-5 text-ow-blue border-gray-300 accent-ow-blue cursor-pointer"
                      />
                      <span className={`font-normal text-base ${disabled ? 'text-gray-400' : 'text-black'}`}>
                        {DeliveryRates[key as keyof typeof DeliveryRates]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <CheckboxWithLabel
              checked={excludeLowArticles}
              onChange={(e) => handleExcludeLowArticlesChange(e.target.checked)}
              disabled={disabled || !alertEnabled}
              label="Exclude companies with 'low article' scores from emails"
              sectionTitle={title}
            />

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <CheckboxWithLabel
                  checked={scoreIncreaseAlertEnabled}
                  onChange={(e) => handleScoreIncreaseAlertEnabledChange(e.target.checked)}
                  disabled={disabled || !alertEnabled}
                  label="If FSS score increases by:"
                  sectionTitle={title}
                />
              </div>
              <div className="flex items-center gap-2 self-start sm:self-center sm:shrink-0">
                <input
                  type="number"
                  className={`h-8 w-20 sm:w-16 border border-gray-300 rounded px-2 py-1 text-base ${!scoreIncreaseAlertEnabled || disabled ? 'text-gray-400' : 'text-black'}`}
                  disabled={!scoreIncreaseAlertEnabled || disabled}
                  value={scoreIncreaseValue}
                  onChange={(e) => handleScoreIncreaseValueChange(e.target.value)}
                  onBlur={handleScoreIncreaseValueBlur}
                  placeholder="10"
                />
                <span className={`text-base self-center ${!scoreIncreaseAlertEnabled || disabled ? 'text-gray-400' : 'text-black'}`}>
                  %
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <CheckboxWithLabel
                  checked={scoreDecreaseAlertEnabled}
                  onChange={(e) => handleScoreDecreaseAlertEnabledChange(e.target.checked)}
                  disabled={disabled || !alertEnabled}
                  label="If FSS score decreases by:"
                  sectionTitle={title}
                />
              </div>
              <div className="flex items-center gap-2 self-start sm:self-center sm:shrink-0">
                <input
                  type="number"
                  className={`h-8 w-20 sm:w-16 border border-gray-300 rounded px-2 py-1 text-base ${!scoreDecreaseAlertEnabled || disabled ? 'text-gray-400' : 'text-black'}`}
                  disabled={!scoreDecreaseAlertEnabled || disabled}
                  value={scoreDecreaseValue}
                  onChange={(e) => handleScoreDecreaseValueChange(e.target.value)}
                  onBlur={handleScoreDecreaseValueBlur}
                  placeholder="10"
                />
                <span className={`text-base self-center ${!scoreDecreaseAlertEnabled || disabled ? 'text-gray-400' : 'text-black'}`}>
                  %
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => {
                const trimmedName = customAlertName.trim();
                if (trimmedName && onSaveCustomAlert) {
                  onSaveCustomAlert(trimmedName, buildSettings({ nameValue: trimmedName }));
                  setCustomAlertName('');
                }
              }}
              disabled={disabled || !customAlertName.trim()}
              className="bg-ow-blue text-white font-medium px-6 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
            >
              Save Custom Alert
            </button>
          </div>
        </div>
      ) : (
        <div className={`grid auto-rows-[1fr] gap-2 ${!alertEnabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`}>
          {showFrequency && (
            <div className="flex flex-row items-center justify-between">
              <label className={`font-normal text-base ${disabled ? 'text-gray-400' : 'text-black'} cursor-pointer self-center mr-2`}>
                Frequency:
              </label>
              <div className="flex flex-row gap-4">
                {Object.keys(DeliveryRates).map((key) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`frequency-${title}`}
                      value={key}
                      checked={selectedFrequency === key}
                      onChange={handleFrequencyChange}
                      disabled={disabled}
                      className="form-radio h-5 w-5 text-ow-blue border-gray-300 accent-ow-blue cursor-pointer"
                    />
                    <span className={`font-normal text-base ${disabled ? 'text-gray-400' : 'text-black'}`}>
                      {DeliveryRates[key as keyof typeof DeliveryRates]}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <CheckboxWithLabel
            checked={excludeLowArticles}
            onChange={(e) => handleExcludeLowArticlesChange(e.target.checked)}
            disabled={disabled || !alertEnabled}
            label="Exclude companies with 'low article' scores from emails"
            sectionTitle={title}
          />

          {showSmartTypes && (
            <>
              <CheckboxWithLabel
                checked={scoreIncreaseAlertEnabled}
                onChange={(e) => handleScoreIncreaseAlertEnabledChange(e.target.checked)}
                disabled={disabled || !alertEnabled}
                label="If FSS score increases"
                sectionTitle={title}
              />

              <CheckboxWithLabel
                checked={scoreDecreaseAlertEnabled}
                onChange={(e) => handleScoreDecreaseAlertEnabledChange(e.target.checked)}
                disabled={disabled || !alertEnabled}
                label="If FSS score decreases"
                sectionTitle={title}
              />
            </>
          )}

          {showThresholds && (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <CheckboxWithLabel
                    checked={scoreIncreaseAlertEnabled}
                    onChange={(e) => handleScoreIncreaseAlertEnabledChange(e.target.checked)}
                    disabled={disabled || !alertEnabled}
                    label="If FSS score increases by:"
                    sectionTitle={title}
                  />
                </div>
                <div className="flex items-center gap-2 self-start sm:self-center sm:shrink-0">
                  <input
                    type="number"
                    className={`h-8 w-20 sm:w-16 border border-gray-300 rounded px-2 py-1 text-base ${!scoreIncreaseAlertEnabled || disabled ? 'text-gray-400' : 'text-black'}`}
                    disabled={!scoreIncreaseAlertEnabled || disabled}
                    value={scoreIncreaseValue}
                    onChange={(e) => handleScoreIncreaseValueChange(e.target.value)}
                    onBlur={handleScoreIncreaseValueBlur}
                    placeholder="10"
                  />
                  <span className={`text-base self-center ${!scoreIncreaseAlertEnabled || disabled ? 'text-gray-400' : 'text-black'}`}>
                    %
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <CheckboxWithLabel
                    checked={scoreDecreaseAlertEnabled}
                    onChange={(e) => handleScoreDecreaseAlertEnabledChange(e.target.checked)}
                    disabled={disabled || !alertEnabled}
                    label="If FSS score decreases by:"
                    sectionTitle={title}
                  />
                </div>
                <div className="flex items-center gap-2 self-start sm:self-center sm:shrink-0">
                  <input
                    type="number"
                    className={`h-8 w-20 sm:w-16 border border-gray-300 rounded px-2 py-1 text-base ${!scoreDecreaseAlertEnabled || disabled ? 'text-gray-400' : 'text-black'}`}
                    disabled={!scoreDecreaseAlertEnabled || disabled}
                    value={scoreDecreaseValue}
                    onChange={(e) => handleScoreDecreaseValueChange(e.target.value)}
                    onBlur={handleScoreDecreaseValueBlur}
                    placeholder="10"
                  />
                  <span className={`text-base self-center ${!scoreDecreaseAlertEnabled || disabled ? 'text-gray-400' : 'text-black'}`}>
                    %
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertSettingsSection;
